import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, MapPin, ArrowLeft } from "lucide-react";
import { STATE_PAGES } from "@/data/content";
import { useSEO } from "@/lib/seo";
import { trackCTA } from "@/lib/analytics";
import CTASection from "@/components/CTASection";

export default function StateTierII() {
  const { state } = useParams();
  const page = STATE_PAGES.find((p) => p.slug === state);

  useSEO({
    title: page ? `Tier II Reporting in ${page.state}` : "Tier II Reporting",
    description: page
      ? `Tier II / EPCRA chemical inventory reporting support for ${page.state} facilities. ${page.intro}`
      : "",
    path: `/tier-ii-reporting/${state}`,
  });

  if (!page) return <Navigate to="/services/tier-ii-reporting-support" replace />;

  return (
    <>
      <section className="relative overflow-hidden bg-navy-900 noise-overlay">
        <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-16 lg:py-20">
          <Link to="/services/tier-ii-reporting-support" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-white transition-colors" data-testid="back-to-tierii">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Tier II Reporting Support
          </Link>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-amber-400">
            <MapPin className="h-3.5 w-3.5" aria-hidden="true" /> {page.state}
          </div>
          <h1 className="mt-6 max-w-3xl font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
            Tier II Reporting Support for {page.state} Facilities
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-300">{page.intro}</p>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-6">
            <p className="text-sm font-bold text-navy-900">Administering agency</p>
            <p className="mt-1 text-sm text-slate-600">{page.agency}</p>
          </div>

          <h2 className="mt-12 font-heading text-2xl font-bold text-navy-900">What {page.state} facilities should know</h2>
          <ul className="mt-6 space-y-4">
            {page.points.map((pt, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`state-point-${i}`}>
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
                <span className="text-base leading-relaxed text-slate-700">{pt}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 rounded-xl border border-amber-200 bg-amber-50 p-6">
            <p className="text-sm leading-relaxed text-slate-700">
              State requirements, portals, deadlines and fees change over time. This page is provided for general
              guidance only. Wasserlab helps prepare {page.state} Tier II submissions, but facilities remain responsible
              for verifying current requirements with the applicable state agency.
            </p>
          </div>

          <div className="mt-12 rounded-xl bg-navy-900 p-8 text-center noise-overlay">
            <h3 className="font-heading text-xl font-bold text-white">Preparing your {page.state} Tier II report?</h3>
            <Link
              to="/free-tier-ii-assessment"
              onClick={() => trackCTA("Assessment", `state-${state}`)}
              className="mt-5 inline-flex items-center gap-2 rounded-md bg-amber-500 px-6 py-3 text-sm font-bold text-navy-900 transition-transform hover:-translate-y-0.5 hover:bg-amber-400"
              data-testid="state-cta-assessment"
            >
              Get a Free Tier II Assessment <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
      <CTASection location={`state-${state}-footer`} />
    </>
  );
}
